/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `obdii_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class ObdiiDataMesg : Mesg {
    public constructor() : super("ObdiiData", Profile.MesgNum.OBDII_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Timestamp message was output */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Fractional part of timestamp, added to timestamp */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in time_offset. */
    public val numTimeOffset: Int
        get() = getField(TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** time_offset, in ms. Offset of PID reading [i] from start_timestamp+start_timestamp_ms. Readings may span accross seconds. */
    public var timeOffset: List<UShort?>?
        get() {
            val field = getField(TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeOffset(it) }
        }
        set(value) {
            removeField(TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeOffset(index, element) }
        }

    public fun getTimeOffset(index: Int): UShort? =
        getFieldValue(TIME_OFFSET_FIELD_NUM, index) as? UShort

    public fun setTimeOffset(index: Int, value: UShort?) {
        setFieldValue(TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** pid. Parameter ID */
    public var pid: UByte?
        get() = getFieldValue(PID_FIELD_NUM) as? UByte
        set(value) = setFieldValue(PID_FIELD_NUM, value)

    /** Number of values stored in raw_data. */
    public val numRawData: Int
        get() = getField(RAW_DATA_FIELD_NUM)?.numValues ?: 0

    /** raw_data. Raw parameter data */
    public var rawData: List<UByte?>?
        get() {
            val field = getField(RAW_DATA_FIELD_NUM) ?: return null
            return List(field.numValues) { getRawData(it) }
        }
        set(value) {
            removeField(RAW_DATA_FIELD_NUM)
            value?.forEachIndexed { index, element -> setRawData(index, element) }
        }

    public fun getRawData(index: Int): UByte? =
        getFieldValue(RAW_DATA_FIELD_NUM, index) as? UByte

    public fun setRawData(index: Int, value: UByte?) {
        setFieldValue(RAW_DATA_FIELD_NUM, value, index)
    }

    /** Number of values stored in pid_data_size. */
    public val numPidDataSize: Int
        get() = getField(PID_DATA_SIZE_FIELD_NUM)?.numValues ?: 0

    /** pid_data_size. Optional, data size of PID[i]. If not specified refer to SAE J1979. */
    public var pidDataSize: List<UByte?>?
        get() {
            val field = getField(PID_DATA_SIZE_FIELD_NUM) ?: return null
            return List(field.numValues) { getPidDataSize(it) }
        }
        set(value) {
            removeField(PID_DATA_SIZE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setPidDataSize(index, element) }
        }

    public fun getPidDataSize(index: Int): UByte? =
        getFieldValue(PID_DATA_SIZE_FIELD_NUM, index) as? UByte

    public fun setPidDataSize(index: Int, value: UByte?) {
        setFieldValue(PID_DATA_SIZE_FIELD_NUM, value, index)
    }

    /** Number of values stored in system_time. */
    public val numSystemTime: Int
        get() = getField(SYSTEM_TIME_FIELD_NUM)?.numValues ?: 0

    /** system_time. System time associated with sample expressed in ms, can be used instead of time_offset. There will be a system_time value for each raw_data element. For multibyte pids the system_time is repeated. */
    public var systemTime: List<UInt?>?
        get() {
            val field = getField(SYSTEM_TIME_FIELD_NUM) ?: return null
            return List(field.numValues) { getSystemTime(it) }
        }
        set(value) {
            removeField(SYSTEM_TIME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSystemTime(index, element) }
        }

    public fun getSystemTime(index: Int): UInt? =
        getFieldValue(SYSTEM_TIME_FIELD_NUM, index) as? UInt

    public fun setSystemTime(index: Int, value: UInt?) {
        setFieldValue(SYSTEM_TIME_FIELD_NUM, value, index)
    }

    /** start_timestamp. Timestamp of first sample recorded in the message. Used with time_offset to generate time of each sample */
    public var startTimestamp: Instant?
        get() = (getFieldValue(START_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** start_timestamp_ms, in ms. Fractional part of start_timestamp */
    public var startTimestampMs: UShort?
        get() = getFieldValue(START_TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(START_TIMESTAMP_MS_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val TIME_OFFSET_FIELD_NUM: UByte = 1u
        public const val PID_FIELD_NUM: UByte = 2u
        public const val RAW_DATA_FIELD_NUM: UByte = 3u
        public const val PID_DATA_SIZE_FIELD_NUM: UByte = 4u
        public const val SYSTEM_TIME_FIELD_NUM: UByte = 5u
        public const val START_TIMESTAMP_FIELD_NUM: UByte = 6u
        public const val START_TIMESTAMP_MS_FIELD_NUM: UByte = 7u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("TimeOffset", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("Pid", 2u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[3] = Field("RawData", 3u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[4] = Field("PidDataSize", 4u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[5] = Field("SystemTime", 5u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[6] = Field("StartTimestamp", 6u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[7] = Field("StartTimestampMs", 7u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            return fields
        }
    }
}
